import { Button, Drawer, Spin, Table } from "antd";
import styles from "./index.module.css";
import closeX from "assets/svg/closeDealsX.svg";
import c2cToken from "assets/svg/c2c-token.svg";
import { CopyButton } from "Components/CopyButton";
import { AllInfoComponent } from "./AllInfoComponent";
import { AdminContext } from "context/adminContext";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NotificationContext } from "context/notificationContext";
import { cancelRent, downloadRentDocument, getRentsDetails } from "helpers/backend_helper";
import { useRequest } from "ahooks";
import { formatDateRange, formatDaysCount, getDaysCount } from "helpers/helper";
import { formatDealDateTime, formatShortDate } from "helpers/format";
import { getIsDateOn, getRightDocumentName, STEPS } from "./utils";
import { ClockCircleOutlined } from "@ant-design/icons";

type Props = {
  close: () => void;
  isOpen: boolean;
  id: string;
  refreshState?: boolean;
};

const CANCEL_RENT_STATUSES = ["BOOKED", "PENDING_APPROVAL", "ACCEPTED", "WAITING_FOR_LESSEE_ACCEPT"];

export const DealsInfo = ({ close, isOpen, id, refreshState }: Props) => {
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const { localCountry } = useContext(AdminContext);
  const [isCanceled, setIsCanceled] = useState(null);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const {
    data,
    loading,
    refreshAsync: refreshRentsDetails,
  } = useRequest(() => getRentsDetails(id, localCountry.iso.toUpperCase()), {
    ready: !!localCountry.iso && !!id,
    refreshDeps: [id, refreshState],
    onError: (err: any) => {
      openNotificationWithIcon("error", err.response?.data?.message || err.message);
    },
    onSuccess: (data) => {
      if (data.status === "CANCELLED" || data.status === "COMPLETED") {
        setIsCanceled(true);
      } else {
        setIsCanceled(false);
      }
    },
  });

  const { loading: loadingCancelRent, run: onCancelRent } = useRequest(
    () => {
      console.log(localCountry.iso);
      return cancelRent(id, localCountry.iso.toUpperCase());
    },
    {
      ready: !!localCountry.iso && !!id,
      manual: true,
      refreshDeps: [id, refreshState],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
      onSuccess: () => {
        refreshRentsDetails();
        openNotificationWithIcon("success", `Rent has been canceled`);
      },
    },
  );

  const handleDownloadDocument = useCallback(
    async (documentId: string) => {
      // Открываем вкладку синхронно (до await), иначе блокировщик попапов
      // не пропустит window.open после асинхронного запроса.
      const newTab = window.open("", "_blank");
      try {
        const blob = await downloadRentDocument(id, documentId);
        const url = URL.createObjectURL(blob);
        if (newTab) {
          newTab.location.href = url;
        } else {
          window.open(url, "_blank");
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (error: any) {
        newTab?.close();
        openNotificationWithIcon("error", error?.response?.data?.message || error?.message);
      }
    },
    [id, openNotificationWithIcon],
  );
  handleDownloadDocument;

  const columns = (lessor, renter) => [
    {
      title: <div style={{ color: "#868686" }}>Step</div>,
      dataIndex: "step",
      key: "step",
    },
    {
      title: <div style={{ color: "#868686" }}>{`Renter, ${renter}`}</div>,
      dataIndex: "renter",
      key: "renter",
    },
    {
      title: " ",
      dataIndex: "renterRequest",
      key: "renterRequest",
    },
    {
      title: <div style={{ color: "#868686" }}>{`Lessor, ${lessor}`}</div>,
      dataIndex: "lessor",
      key: "lessor",
    },
    {
      title: " ",
      dataIndex: "lessorRequest",
      key: "lessorRequest",
    },
    {
      title: <div style={{ color: "#868686", width: "100%", textAlign: "end" }}>Documents</div>,
      dataIndex: "document",
      key: "document",
    },
  ];

  const source = useMemo(() => {
    return data?.rentSteps.map((v, i) => {
      return v.step === "cancellation"
        ? {
            key: i,
            step: "",
            renter: v.lessee.temp ? "" : <span style={{ color: "#F40909" }}>{v.lessee.action}</span>,
            renterRequest: v.lessee.temp ? (
              ""
            ) : (
              <span style={{ color: "#F40909" }}>{formatDealDateTime(v.lessee.date)}</span>
            ),
            lessor: "",
            lessorRequest: "",
            document: !v?.lessor?.temp && !v?.lessee?.temp && (
              <div style={{ color: "#F40909", margin: "0 0 0 auto", width: "fit-content" }}>Refund</div>
            ),
          }
        : v.step === "dispute_request"
        ? {
            key: i,
            step: STEPS[v.step],
            renter:
              v?.lessee?.action && v?.lessee?.temp ? (
                <div className={styles.lessor}>
                  <ClockCircleOutlined />
                  <div className={styles.waiting + " " + styles.showAllTime}>{v.lessee.action}</div>
                </div>
              ) : (
                <span>{v?.lessee?.action}</span>
              ),
            renterRequest:
              !(v?.lessee?.action && v?.lessee?.temp) && v?.lessee?.date ? formatDealDateTime(v.lessee.date) : null,

            lessor:
              v?.lessor?.action && v?.lessor?.temp ? (
                <div className={styles.lessor}>
                  <ClockCircleOutlined />
                  <div className={styles.waiting + " " + styles.showAllTime}>{v.lessor.action}</div>
                </div>
              ) : (
                <span>{v?.lessor?.action}</span>
              ),
            lessorRequest:
              !(v?.lessor?.action && v?.lessor?.temp) && v?.lessor?.date ? formatDealDateTime(v.lessor.date) : null,
            document: !v?.lessor?.temp && !v?.lessee?.temp && v?.documents?.[0]?.documentId && (
              <div style={{ color: "#EDA10E", margin: "0 0 0 auto", width: "fit-content" }}>
                <a
                  onClick={() => handleDownloadDocument(v?.documents?.[0].id)}
                  style={{
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  {getRightDocumentName(v?.step)}
                </a>
              </div>
            ),
          }
        : {
            key: i,
            step: STEPS[v.step],
            renter:
              v?.lessee?.action && v?.lessee?.temp ? (
                <div
                  className={
                    v.step === "product_return"
                      ? styles.lessor + " " + styles.showAll
                      : v.step === "product_transfer"
                      ? styles.lessor + " " + styles.showAll
                      : styles.lessor + " " + styles.showAll
                  }
                >
                  <ClockCircleOutlined />
                  <div
                    className={
                      v.step === "product_return"
                        ? styles.waiting + " " + styles.showAllLongTime
                        : v.step === "product_transfer"
                        ? styles.waiting + " " + styles.showAllLongTimeTransfer
                        : styles.waiting + " " + styles.showAllTime
                    }
                  >
                    {v.lessee.action}
                    {v.lessee.date &&
                      getIsDateOn(v.step) &&
                      ` on ${formatShortDate(new Date(v.lessee.date).setUTCHours(0, 0, 0, 0))}`}
                  </div>
                </div>
              ) : (
                <span>{v?.lessee?.action}</span>
              ),
            renterRequest:
              !(v?.lessee?.action && v?.lessee?.temp) && v?.lessee?.date ? formatDealDateTime(v.lessee.date) : null,

            lessor:
              v?.lessor?.action && v?.lessor?.temp ? (
                <div
                  className={
                    v.step === "product_return"
                      ? styles.lessor + " " + styles.showAll
                      : v.step === "product_transfer"
                      ? styles.lessor + " " + styles.showAll
                      : styles.lessor
                  }
                >
                  <ClockCircleOutlined />
                  <div
                    className={
                      v.step === "product_return"
                        ? styles.waiting + " " + styles.showAllLongTime
                        : v.step === "product_transfer"
                        ? styles.waiting + " " + styles.showAllLongTimeTransfer
                        : styles.waiting + " " + styles.showAllTime
                    }
                  >
                    {v.lessor.action}
                    {v.lessor.date &&
                      getIsDateOn(v.step) &&
                      ` on ${formatShortDate(new Date(v.lessor.date).setUTCHours(0, 0, 0, 0))}`}
                  </div>
                </div>
              ) : (
                <span>{v?.lessor?.action}</span>
              ),
            lessorRequest:
              !(v?.lessor?.action && v?.lessor?.temp) && v?.lessor?.date ? formatDealDateTime(v.lessor.date) : null,
            document: !v?.lessor?.temp && !v?.lessee?.temp && v?.documents?.[0]?.documentId && (
              <div style={{ color: "#EDA10E", margin: "0 0 0 auto", width: "fit-content" }}>
                <a
                  onClick={() => handleDownloadDocument(v?.documents?.[0].id)}
                  style={{
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  {getRightDocumentName(v?.step)}
                </a>
              </div>
            ),
          };
    });
  }, [data?.rentSteps]);

  return (
    <Drawer
      placement="bottom"
      closable={false}
      open={isOpen}
      className={styles.draver}
      classNames={{
        mask: styles.mask,
        wrapper: styles.wrapperDraver,
      }}
    >
      {loading || !data ? (
        <>
          <Button onClick={close} type="text" className={styles.closeBtn}>
            Close <img src={closeX} alt="close" width={24} height={24} />
          </Button>
          <Spin style={{ left: "50%", top: "50%", transform: "translate(50% -50%)", width: "100%", height: "100%" }} />
        </>
      ) : (
        <div className={styles.container}>
          <div className={styles.topInfoWrapper}>
            <div className={styles.mainInfo}>
              <span>Deal Info</span>
              <span>ID</span>
              <CopyButton copyText="copied" text={id} wait={500} className={styles.copyBtn} />
              <Button type="primary" className={styles.buttonChat}>
                Chat
              </Button>
              {isCanceled === false && CANCEL_RENT_STATUSES.includes(data.status) && (
                <Button onClick={onCancelRent} loading={loadingCancelRent} type="primary" className={styles.buttonChat}>
                  Cancel
                </Button>
              )}
            </div>
            <Button onClick={close} type="text" className={styles.closeBtn}>
              Close <img src={closeX} alt="close" width={24} height={24} />
            </Button>
          </div>
          <div className={styles.allInfo}>
            <AllInfoComponent
              contentFirst={[
                { content: data.productInfo.productName },
                { label: "ID", content: data.productInfo.productId, isContentID: true, sliceLength: 7 },
              ]}
              label="Product"
            />
            <AllInfoComponent
              contentFirst={[
                { content: data.lesseeInfo.firstName + " " + data.lesseeInfo.lastName },
                { label: "ID", content: data.lesseeInfo.id, isContentID: true, sliceLength: 7 },
              ]}
              label="Renter"
            />
            <AllInfoComponent
              contentFirst={[
                { content: data.lessorInfo.firstName + " " + data.lessorInfo.lastName },
                { label: "ID", content: data.lessorInfo.id, isContentID: true, sliceLength: 7 },
              ]}
              label="Lessor"
            />
            <AllInfoComponent
              contentFirst={[
                {
                  // content: getPeriod(data.transferStartDate, data.returnTransferDate),
                  content: `${formatDateRange(
                    new Date(data.transferStartDate),
                    new Date(data.returnTransferDate),
                  )}, ${formatDaysCount(getDaysCount(data.transferStartDate, data.returnTransferDate))}`,
                  label: "Period",
                },
                { label: "Amount", content: "€" + data.amount },
              ]}
              label="Deal"
            />
            <AllInfoComponent
              contentFirst={[
                { content: (+data.insurance.value).toString(), label: "Rate" },
                {
                  label: data.insurance.insuranceType === "PERCENTAGE" ? "Fee" : "Deposit",
                  content: (
                    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {data.method.payIn?.toLowerCase() === "wallet" ? (
                        <img src={c2cToken} alt="c2c token" width={12} height={14} />
                      ) : (
                        "€"
                      )}
                      {data.insurance.insuranceType === "DEPOSIT" ? data.insurance.deposit : data.insurance.value}
                    </div>
                  ),
                },
              ]}
              label="Insurance"
            />
            <AllInfoComponent
              style={{ marginRight: 40 }}
              contentFirst={[
                { content: data.method.payIn, label: "Pay-in" },
                { label: "Pay-out", content: data.method.payOut },
              ]}
              label="Method"
            />
          </div>
          <Table
            pagination={false}
            className={styles.table}
            columns={columns(data.lessorInfo.firstName, data.lesseeInfo.firstName)}
            dataSource={source}
          />
        </div>
      )}
    </Drawer>
  );
};
